import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categoryArticles, categoryInfo } from '@/mocks/category';
import { Helmet } from '@/pages/home/components/Helmet';

const tabs = [
  { id: 'meizhuang', label: '国产美妆', icon: 'ri-magic-line' },
  { id: 'shuma', label: '国产数码', icon: 'ri-smartphone-line' },
  { id: 'shipin', label: '国产食品', icon: 'ri-bowl-line' },
  { id: 'fushi', label: '国产服饰', icon: 'ri-t-shirt-line' },
];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'meizhuang';
  const [visibleArticles, setVisibleArticles] = useState(6);

  useEffect(() => {
    setVisibleArticles(6);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const currentInfo = categoryInfo[activeTab];
  const currentArticles = categoryArticles[activeTab] || [];
  const displayedArticles = currentArticles.slice(0, visibleArticles);
  const hasMore = visibleArticles < currentArticles.length;

  const tabLabel = tabs.find((t) => t.id === activeTab)?.label || '';

  const seoTitle = `${tabLabel} | 国产专区 - 优质国货精选推荐`;
  const seoDesc = currentInfo
    ? `${currentInfo.title}：${currentInfo.description.substring(0, 120)}`
    : '国产专区分类页面，精选国产好物推荐。';

  return (
    <>
      <Helmet
        title={seoTitle}
        description={seoDesc}
        keywords={`${tabLabel},国产专区,国产品牌,国货推荐,国产好物`}
        canonicalUrl={`https://www.guochanzhuanqu.cn/category?tab=${activeTab}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          'name': `${tabLabel} - 国产专区`,
          'description': seoDesc,
          'url': `https://www.guochanzhuanqu.cn/category?tab=${activeTab}`,
          'inLanguage': 'zh-CN',
          'breadcrumb': {
            '@type': 'BreadcrumbList',
            'itemListElement': [
              { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.guochanzhuanqu.cn/' },
              { '@type': 'ListItem', 'position': 2, 'name': tabLabel, 'item': `https://www.guochanzhuanqu.cn/category?tab=${activeTab}` },
            ],
          },
        }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '分类' }, { label: tabLabel }]} />

        {currentInfo && (
          <section className="px-4 md:px-6 py-6" aria-labelledby="cat-title">
            <div className="max-w-7xl mx-auto">
              <div className="relative rounded-lg overflow-hidden h-[200px] md:h-[300px]">
                <img
                  src={currentInfo.image}
                  alt={currentInfo.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background-900/70 to-transparent flex items-center">
                  <div className="px-6 md:px-10 max-w-lg">
                    <h1 id="cat-title" className="text-2xl md:text-3xl font-heading font-bold text-white">
                      {currentInfo.title}
                    </h1>
                    <p className="mt-2 text-sm text-white/80 leading-relaxed hidden sm:block">
                      {currentInfo.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="px-4 md:px-6 pb-3">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="分类切换">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => {
                    setSearchParams({ tab: tab.id });
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-6" aria-labelledby="articles-heading">
          <div className="max-w-7xl mx-auto">
            <h2 id="articles-heading" className="text-xl font-heading font-bold text-foreground-900 mb-5">
              {tabLabel}推荐文章
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {displayedArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-200">
                      <span className="text-xs text-foreground-500">
                        <i className="ri-calendar-line mr-1"></i>{article.date}
                      </span>
                      <span className="text-xs text-foreground-500">
                        <i className="ri-time-line mr-1"></i>{article.readTime}
                      </span>
                      <span className="text-xs text-foreground-500">
                        <i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setVisibleArticles((prev) => prev + 6)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  加载更多 <i className="ri-arrow-down-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}